import { createContext, useCallback, useState } from "react";
import { coerceToString } from "@/utils/utils";
import type { CommercialReceptionBookViewPlan } from "@/types/commercialReceptionBookViewPlan";

interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  isFree: boolean;
  isStaff: boolean;
  email: string;
  isReadOnly?: boolean;
  isSharable: boolean;
  commercialReceptionBookViewPlan?: CommercialReceptionBookViewPlan;
  ssoPartnerIds: number[];
}

interface UserDataContext {
  data: UserData;
  setUserData: (current: UserData) => void;
}

const getDefaultUseData = (): UserData => {
  const storedData = localStorage.getItem("user");
  const storedUserData = storedData
    ? (JSON.parse(storedData) as Record<string, unknown>)
    : null;
  const rawSsoPartnerIds = storedUserData?.ssoPartnerIds;
  return {
    id: coerceToString(storedUserData?.id),
    firstName:
      typeof storedUserData?.firstName === "string"
        ? storedUserData.firstName
        : "",
    lastName:
      typeof storedUserData?.lastName === "string"
        ? storedUserData.lastName
        : "",
    isFree:
      typeof storedUserData?.isFree === "boolean"
        ? storedUserData.isFree
        : false,
    isStaff:
      typeof storedUserData?.isStaff === "boolean"
        ? storedUserData.isStaff
        : false,
    email:
      typeof storedUserData?.email === "string" ? storedUserData.email : "",
    isReadOnly:
      typeof storedUserData?.isReadOnly === "boolean"
        ? storedUserData.isReadOnly
        : false,
    isSharable:
      typeof storedUserData?.isSharable === "boolean"
        ? storedUserData.isSharable
        : false,
    commercialReceptionBookViewPlan:
      storedUserData?.commercialReceptionBookViewPlan === "pay_per_view" ||
      storedUserData?.commercialReceptionBookViewPlan === "flat_monthly"
        ? storedUserData.commercialReceptionBookViewPlan
        : undefined,
    ssoPartnerIds: Array.isArray(rawSsoPartnerIds)
      ? rawSsoPartnerIds.filter((v): v is number => typeof v === "number")
      : [],
  };
};

const defaultContext: UserDataContext = {
  data: getDefaultUseData(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUserData: () => {},
};

export const userDataContext = createContext<UserDataContext>(defaultContext);

export const useUserData = (): UserDataContext => {
  const [data, setData] = useState<UserData>(getDefaultUseData());

  const setUserData = useCallback((current: UserData): void => {
    setData(current);
  }, []);

  return { data, setUserData };
};
